import React, { useEffect, useState } from "react";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import { Link, useSearchParams } from "react-router-dom";
import API_BASE_URL from "../../config/apiConfig";
import search from "../../assets/images/search.svg";
import avatar from "../../assets/images/icon-avatar.svg";
import arrowLeft from "../../assets/images/arrow-left.svg";
import arrowRight from "../../assets/images/arrow-right.svg";
import Swal from "sweetalert2";
import ToggleSwitch from "../../components/ToggleSwitch";

interface Therapist {
  user_id: string;
  firstname: string;
  lastname: string;
  email: string;
  is_active: boolean;
  therapist_category?: string;
}

interface PagedResponse {
  items: Therapist[];
  total_count: number;
  page: number;
  page_size: number;
}

interface TherapistCategory {
  key: string;
  value: string;
  priority: string;
  color: string;
}

const TherapistsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 1;
  
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const pageSize = 20;
  const [totalCount, setTotalCount] = useState<number>(0);

  // Stare pentru cautare
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Stare pentru categorii
  const [categories, setCategories] = useState<TherapistCategory[]>([]);

  // Noua stare pentru toggle inactive (initial, inactive sunt ascunse)
  const [showInactive, setShowInactive] = useState<boolean>(() => {
    return localStorage.getItem("showInactiveTherapists") === "true";
  });
  useEffect(() => {
    localStorage.setItem("showInactiveTherapists", String(showInactive));
  }, [showInactive]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const getVisiblePages = (totalPages: number): (number | string)[] => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    return [1, 2, 3, "...", totalPages - 2, totalPages - 1, totalPages];
  };

  // Functie helper pentru a returna categoria
  const getCategoryValue = (key: string | undefined): string => {
    if (!key) return "-";
    const cat = categories.find(c => c.key === key);
    return cat ? cat.value : key;
  };

  // Fetch terapeut (cu search sau paginat)
  useEffect(() => {
    const fetchTherapists = async () => {
      setLoading(true);
      try {
        if (searchTerm.length >= 3) {
          const response = await fetch(
            `${API_BASE_URL}/users/therapists/search?term=${encodeURIComponent(searchTerm)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const data: Therapist[] = await response.json();
          setTherapists(data);
          setTotalCount(data.length);
        } else {
          const response = await fetch(
            `${API_BASE_URL}/users/therapists/paged?page=${currentPage}&page_size=${pageSize}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const data: PagedResponse = await response.json();
          setTherapists(data.items);
          setTotalCount(data.total_count);
          setSearchParams({ page: String(currentPage) });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTherapists();
  }, [currentPage, setSearchParams, searchTerm]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/types/therapist-categories`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await response.json();
        setCategories(data.data || []);
      } catch (error) {
        console.error("Eroare la încărcarea categoriilor:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleToggleActive = async (userId: string) => {
    try {
      await fetch(`${API_BASE_URL}/users/therapist/${userId}/toggle-active`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setTherapists((prev) =>
        prev.map((therapist) =>
          therapist.user_id === userId
            ? { ...therapist, is_active: !therapist.is_active }
            : therapist
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (userId: string) => {
    const result = await Swal.fire({
      title: "Ești sigur?",
      text: "Vrei să ștergi acest terapeut?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6C298B",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Da, șterge-l!",
      cancelButtonText: "Anulează",
    });

    if (result.isConfirmed) {
      try {
        await fetch(`${API_BASE_URL}/users/therapist/${userId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setTherapists((prev) =>
          prev.filter((therapist) => therapist.user_id !== userId)
        );
        Swal.fire("Șters!", "Terapeutul a fost șters.", "success");
      } catch (error) {
        console.error(error);
        Swal.fire("Eroare!", "Nu s-a putut șterge terapeutul deoarece are programări alocate.", "error");
      }
    }
  };

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchOpen(false);
  };

  // Filtrare: daca showInactive e false, afisam doar terapeutii activi
  const displayedTherapists = showInactive ? therapists : therapists.filter(t => t.is_active);

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />

      <div className="page-content">
        <h1 className="page-title">Adaugă și Editează Terapeuți</h1>
        <div className="table-top">
          <div className="page-subtitle">Echipa</div>
          <div className="table-actions">
            <div id="therapistsSearch" className="search-container">
              {searchOpen ? (
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Caută terapeut..."
                  autoFocus
                />
              ) : (
                <img
                  src={search}
                  alt="search"
                  onClick={() => setSearchOpen(true)}
                  style={{ cursor: "pointer" }}
                />
              )}
              {searchOpen && (
                <button onClick={clearSearch} className="clear-search">
                  x
                </button>
              )}
            </div>
            <ToggleSwitch
              checked={showInactive}
              onChange={setShowInactive}
              labelOn="Arată inactivi"
              labelOff="Arată inactivi"
            />
            <Link to="/webmaster/therapists/add-therapist">
              <button className="primary-btn">Adaugă Terapeut</button>
            </Link>
           
          </div>
        </div>
        {loading ? (
          <p>Se încarcă...</p>
        ) : displayedTherapists.length === 0 ? (
          <p>Nu există terapeuți înregistrați.</p>
        ) : (
          <div>
            <div className="table-rounded-container">
              <table className="table-rounded">
                <thead>
                  <tr>
                    <th className="column-name">Nume / Prenume</th>
                    <th>Email</th>
                    <th>Tip încadrare</th>
                    <th>Status</th>
                    <th className="column-actions">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTherapists.map((therapist) => (
                    <tr key={therapist.user_id}>
                      <td>
                        <img src={avatar} alt="terapeut" className="user-icon" />{" "}
                        {therapist.firstname} {therapist.lastname}
                      </td>
                      <td>{therapist.email}</td>
                      <td>{getCategoryValue(therapist.therapist_category)}</td>
                      <td>
                        <button
                          onClick={() => handleToggleActive(therapist.user_id)}
                          className={therapist.is_active ? "active" : "inactive"}
                        >
                          {therapist.is_active ? "Activ" : "Inactiv"} <span className="button-dot">&#x2022;</span>
                        </button>
                      </td>
                      <td className="column-actions">
                        <button onClick={() => handleDelete(therapist.user_id)}>Șterge</button>
                        <Link to={`/webmaster/therapists/edit-therapist/${therapist.user_id}`}>
                          <button>Editează</button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {searchTerm.length < 3 && (
                <div className="pagination">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    <img src={arrowLeft} alt="inapoi" className="pagination-left" /> Înapoi
                  </button>
                  <div className="pages">
                    {getVisiblePages(totalPages).map((pageItem, index) => {
                      if (pageItem === "...") {
                        return <span key={index} className="pagination-ellipsis">{pageItem}</span>;
                      }
                      return (
                        <button
                          key={index}
                          className={currentPage === pageItem ? "active-page" : ""}
                          onClick={() => goToPage(Number(pageItem))}
                        >
                          {pageItem}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    Înainte <img src={arrowRight} alt="Înainte" className="pagination-right" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapistsPage;
